import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonMultiprocessing = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Multiprocessing: True Parallelism</h1>
                <p className="subtitle">Bypassing the GIL to unleash the full power of Multi-Core CPUs.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        In <strong>Threading</strong>, we had 4 chefs fighting over 1 Stove.
                        <strong>Multiprocessing</strong> builds 4 separate Kitchens (Processes).
                    </p>
                    <ul>
                        <li><strong>The Power:</strong> Each kitchen has its own Stove (CPU Core). They cook completely independently.</li>
                        <li><strong>The Cost:</strong> Building a kitchen is expensive (Slow startup). Also, since they are in different rooms, Chef A can't easily hand a plate to Chef B (Memory is not shared). They need a special window (Queue/Pipe) to pass things.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. Syntax: Creating Processes</h2>
                <p>
                    It looks similar to Threading, but under the hood, it's cloning the entire Python interpreter.
                </p>
                <CodeBlock language="python" code={`from multiprocessing import Process

def heavy_calculation(name):
    # This runs on a separate CPU Core!
    total = sum(i * i for i in range(10_000_000))
    print(f"{name} finished: {total}")

if __name__ == "__main__":
    #  CRITICAL: This guard protects against infinite recursion on Windows
    
    p1 = Process(target=heavy_calculation, args=("Core-1",))
    p2 = Process(target=heavy_calculation, args=("Core-2",))
    
    p1.start()
    p2.start()
    p1.join()
    p2.join()`} />
                <ProTip><p><strong>Windows User Alert:</strong> You MUST wrap your process creation code in `if __name__ == "__main__":`. If you don't, your computer might crash because Windows re-imports the script for every new process.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>3. Communication: Queues and Pipes</h2>
                <p>
                    Processes don't share global variables. To send data back, use a `Queue`.
                </p>
                <CodeBlock language="python" code={`from multiprocessing import Process, Queue

def worker(q):
    q.put("Result from Process")

if __name__ == "__main__":
    q = Queue()
    p = Process(target=worker, args=(q,))
    p.start()
    
    print(q.get()) # Reads "Result from Process"
    p.join()`} />
            </div>

            <div className="content-section">
                <h2>4. Shared Memory (Advanced)</h2>
                <p>
                    Copying data via Queues is slow for huge arrays (e.g., 4K Video Frames).
                    You can cheat the distinct-memory rule using `Value` (for numbers) or `Array` (for lists).
                </p>
                <CodeBlock language="python" code={`from multiprocessing import Process, Value

def increment(counter):
    with counter.get_lock(): # Yes, we still need locks!
        counter.value += 1

if __name__ == "__main__":
    counter = Value('i', 0) # 'i' = Integer
    
    procs = [Process(target=increment, args=(counter,)) for _ in range(10)]
    
    for p in procs: p.start()
    for p in procs: p.join()
    
    print(counter.value) # 10`} />
            </div>

            <div className="content-section">
                <h2>5. Modern Pattern: ProcessPoolExecutor</h2>
                <p>
                    Just like Threading, don't use raw `Process` classes unless you have to. Use the Executor to map a function over a large dataset.
                </p>
                <CodeBlock language="python" code={`from concurrent.futures import ProcessPoolExecutor
import os
import { ProTip } from '../../components/LessonUI';

def waste_cpu(x):
    # Returns PID so we can prove it's different processes
    return f"Input {x} processed by PID {os.getpid()}"

if __name__ == "__main__":
    # Uses all available CPU cores by default
    with ProcessPoolExecutor() as executor:
        results = executor.map(waste_cpu, range(5))
        
    for res in results:
        print(res)`} />
            </div>

        </div>
    );
};

export default PythonMultiprocessing;

